<script setup lang="ts">
import { getMetaTags } from '~/utils/meta'
import type { BreadcrumbItem } from '@nuxt/ui'
import { supervisoryBoardImages, teamMemberImages } from '~/models/images'
import { pdfs } from '~/models/files'
import type { SupervisoryBoardMemberKey, TeamMemberKey } from '~/types/layouts'
import type { ImageProps } from '~/types/images'
import { heroImages } from '#shared/models/images'

definePageMeta({
  layout: false
})

defineRouteRules({
  prerender: true
})

const { t, tm, baseKey } = useI18n('pages.static.organisatie')

const heroImage = heroImages['ilse-en-frans-1']

useSeoMeta(
  getMetaTags(
    {
      title: '.meta.title',
      description: '.meta.description'
    },
    heroImage.src,
    baseKey
  )
)

useSchemaOrg(
  getSchemaOrg(
    {
      title: '.title',
      type: 'AboutPage',
      breadcrumbs: [{ name: '.title' }]
    },
    baseKey
  )
)

const breadcrumbItems: BreadcrumbItem[] = [{ label: '.title', to: 'organisatie' }]

interface TeamMemberItem {
  name: string
  job: string
  image: ImageProps
  mainJob?: string
}

const teamItems = computed<TeamMemberItem[]>(() => {
  const team: Record<TeamMemberKey, TeamMemberItem> = tm('.section-three.team-members')

  return Object.entries(team).map<TeamMemberItem>(([key, value]) => {
    const alt = t('.section-three.photo', { name: value.name })
    return {
      name: value.name,
      image: {
        src: teamMemberImages[key as TeamMemberKey],
        alt: {
          nl: alt,
          en: alt
        }
      },
      job: value.job
    }
  })
})

interface SupervisoryBoardItem {
  name: string
  job: string
  'main-job': string
}

const supervisoryBoard = computed<TeamMemberItem[]>(() => {
  const items: Record<SupervisoryBoardMemberKey, SupervisoryBoardItem> = tm(
    '.section-three.supervisory-board'
  )

  return Object.entries(items).map<TeamMemberItem>(([key, value]) => {
    const alt = t('.section-three.photo', { name: value.name })
    return {
      name: value.name,
      image: {
        src: supervisoryBoardImages[key as SupervisoryBoardMemberKey],
        alt: {
          nl: alt,
          en: alt
        }
      },
      job: value.job,
      mainJob: value['main-job']
    }
  })
})

const regulationsPDF = pdfs.organisatie['reglement-raad-van-toezicht']
const integrityPolicyPDF = pdfs['integriteitsbeleid']
const policyPlanPDF = pdfs.organisatie['beleidsplan']
const socialEtiquettePDF = pdfs.thuiskok['sociale-omgangsregels']
const articlesOfAssociation = pdfs.organisatie['statuten']
const volunteerPolicyPDF = pdfs['vrijwilligersbeleid']

const hygienePDF = pdfs.thuiskok['hygiene-regels']

const impactStudies2025PDF = pdfs.impactonderzoeken['impactonderzoek-2025']
const impactStudies2024PDF = pdfs.infographic['resultaten-2024']

const annualReport2021PDF = pdfs.organisatie['jaarverslag-2021']
const annualReport2022PDF = pdfs.organisatie['jaarverslag-2022']
const annualReport2023PDF = pdfs.organisatie['jaarverslag-2023']
const annualReport2024PDF = pdfs.organisatie['jaarverslag-2024']
const annualReport2025PDF = pdfs.organisatie['jaarverslag-2025']
</script>

<template>
  <NuxtLayout name="default" title=".title" description=".description" :image="heroImage">
    <VBreadcrumb :items="breadcrumbItems" />

    <VSection>
      <p>{{ t('.section-one.description-one') }}</p>
      <p>{{ t('.section-one.description-two') }}</p>
    </VSection>

    <ExperiencesOfOtherFoodiesSection />

    <VSection base-key=".section-two" title=".title">
      <p>{{ t('.section-two.description-one') }}</p>
      <p>{{ t('.section-two.description-two') }}</p>
      <p>{{ t('.section-two.description-three') }}</p>
    </VSection>

    <VSection base-key=".section-three" title=".title" description=".description">
      <template #after>
        <h3 class="mt-8 mb-4 text-center text-h3">
          {{ t('.section-three.team-members-title') }}
        </h3>

        <VCarousel
          v-slot="{ item }"
          :items="teamItems"
          :number-of-items="{ mobile: 1, tablet: 2, desktop: 3, widescreen: 4 }"
          loop
          dots
          :ui="{ dots: '-bottom-20 md:-bottom-7' }"
          sr-label=".carousel-aria-label"
        >
          <div class="flex flex-col items-center justify-center">
            <VImage
              :image="item.image"
              class="m-0! aspect-square h-60 rounded-full object-cover"
              sizes="240px"
            />
            <h2 class="mt-4 title mb-0! text-h4">
              {{ item.name }}
            </h2>
            <p class="text-center font-semibold">
              {{ item.job }}
            </p>
          </div>
        </VCarousel>

        <h3 class="mt-8 mb-4 text-center text-h3">
          {{ t('.section-three.supervisory-board-title') }}
        </h3>

        <VCarousel
          v-slot="{ item }"
          :items="supervisoryBoard"
          :number-of-items="{ mobile: 1, tablet: 2, desktop: 3, widescreen: 3 }"
          loop
          dots
          sr-label=".carousel-aria-label"
        >
          <div class="flex flex-col items-center justify-center">
            <VImage
              :image="item.image"
              class="m-0! aspect-square h-60 rounded-full object-cover"
              sizes="240px"
            />
            <h2 class="mt-4 title mb-0! text-h4">
              {{ item.name }}
            </h2>
            <p class="font-semibold">
              {{ item.job }}
            </p>
            <p class="mt-2 text-center">
              {{ item.mainJob }}
            </p>
          </div>
        </VCarousel>
      </template>
    </VSection>

    <VSection base-key=".section-four" title=".title">
      <VI18n key-path=".description">
        <template #link>
          <VLink
            label=".link"
            download=""
            :href="annualReport2025PDF"
            target="_blank"
            rel="noopener noreferrer"
          />
        </template>
      </VI18n>
    </VSection>

    <VSection base-key=".section-five" title=".title">
      <VI18n key-path=".description">
        <template #link>
          <VLink
            label=".link"
            download=""
            :href="policyPlanPDF"
            target="_blank"
            rel="noopener noreferrer"
          />
        </template>
      </VI18n>
    </VSection>

    <VSection base-key=".section-six" title=".title" description=".description" />

    <VSection base-key=".section-seven" title=".title" description=".description" />

    <VSection base-key=".section-eight" title=".title">
      <p>{{ t('.section-eight.description-one') }}</p>
      <VI18n key-path=".description-two">
        <template #link-integrity>
          <VLink
            label=".link-integrity"
            download=""
            :href="integrityPolicyPDF"
            target="_blank"
            rel="noopener noreferrer"
          />
        </template>
        <template #link-social>
          <VLink
            label=".link-social"
            download=""
            :href="socialEtiquettePDF"
            target="_blank"
            rel="noopener noreferrer"
          />
        </template>
        <template #link-hygiene>
          <VLink
            label=".link-hygiene"
            download=""
            :href="hygienePDF"
            target="_blank"
            rel="noopener noreferrer"
          />
        </template>
      </VI18n>
      <p>
        {{ t('.section-eight.description-three') }}
      </p>
    </VSection>

    <VSection base-key=".section-nine" title=".title">
      <p>{{ t('.section-nine.description-one') }}</p>
      <VI18n key-path=".description-two">
        <template #link>
          <VLink
            label=".link"
            download=""
            :href="volunteerPolicyPDF"
            target="_blank"
            rel="noopener noreferrer"
          />
        </template>
      </VI18n>
    </VSection>

    <VSection :ui="{ description: '*:w-fit' }">
      <h3 class="mb-0 text-h3">{{ t('.misc-info.contact.title') }}</h3>
      <p class="flex flex-col">
        <VLink label=".misc-info.contact.phone" href="tel:085-0608768" />
        <VLink label=".misc-info.contact.email" href="mailto:info@thuisgekookt.nl" />
        <span>{{ t('.misc-info.contact.kvk') }}</span>
        <span>{{ t('.misc-info.contact.rsin') }}</span>
      </p>

      <h3 class="mb-0 text-h3">{{ t('.misc-info.strategy.title') }}</h3>
      <VLink
        label=".misc-info.strategy.strategy-link"
        download=""
        :href="policyPlanPDF"
        target="_blank"
        rel="noopener noreferrer"
      />

      <h3 class="mb-0 text-h3">{{ t('.misc-info.impact-studies.title') }}</h3>
      <p class="flex flex-col">
        <VLink
          label=".misc-info.impact-studies.link-2025"
          download=""
          :href="impactStudies2025PDF"
          target="_blank"
          rel="noopener noreferrer"
        />
        <VLink
          label=".misc-info.impact-studies.link-2024"
          download=""
          :href="impactStudies2024PDF"
          target="_blank"
          rel="noopener noreferrer"
        />
      </p>

      <h3 class="mb-0 text-h3">{{ t('.misc-info.annual-reports.title') }}</h3>
      <p class="flex flex-col">
        <VLink
          label=".misc-info.annual-reports.link-2025"
          download=""
          :href="annualReport2025PDF"
          target="_blank"
          rel="noopener noreferrer"
        />
        <VLink
          label=".misc-info.annual-reports.link-2024"
          download=""
          :href="annualReport2024PDF"
          target="_blank"
          rel="noopener noreferrer"
        />
        <VLink
          label=".misc-info.annual-reports.link-2023"
          download=""
          :href="annualReport2023PDF"
          target="_blank"
          rel="noopener noreferrer"
        />
        <VLink
          label=".misc-info.annual-reports.link-2022"
          download=""
          :href="annualReport2022PDF"
          target="_blank"
          rel="noopener noreferrer"
        />
        <VLink
          label=".misc-info.annual-reports.link-2021"
          download=""
          :href="annualReport2021PDF"
          target="_blank"
          rel="noopener noreferrer"
        />
      </p>

      <p class="mt-8 flex flex-col">
        <VLink
          label=".misc-info.misc.articles-of-association"
          download=""
          :href="articlesOfAssociation"
          target="_blank"
          rel="noopener noreferrer"
        />
        <VLink
          label=".misc-info.misc.supervisory-board-regulations"
          download=""
          :href="regulationsPDF"
          target="_blank"
          rel="noopener noreferrer"
        />
        <VLink
          label=".misc-info.misc.terms-and-conditions"
          :to="{ name: 'algemene-voorwaarden' }"
        />
      </p>
    </VSection>
  </NuxtLayout>
</template>

<style scoped></style>
